export const CALLBACK_CATEGORIES = [
  'Agent lifecycle',
  'Conversation lifecycle',
  'Customer input and refinements',
  'Response lifecycle',
  'Product results',
  'Add to Basket',
  'Other interactions',
] as const;

export type CallbackCategory = (typeof CALLBACK_CATEGORIES)[number];

export type CallbackName =
  | 'onAgentLoaded'
  | 'onAgentOpen'
  | 'onAgentClose'
  | 'onAgentExpand'
  | 'onAgentRestore'
  | 'onAgentDestroyed'
  | 'onConversationStarted'
  | 'onConversationRestored'
  | 'onConversationReset'
  | 'onPromptSubmit'
  | 'onSuggestionClick'
  | 'onRefinementDisplayed'
  | 'onRefinementClick'
  | 'onResponseStarted'
  | 'onResponseCompleted'
  | 'onProductGroupDisplayed'
  | 'onProductDisplayed'
  | 'onProductClick'
  | 'onAddToBasketClick'
  | 'onAddToBasketSuccess'
  | 'onAddToBasketError'
  | 'onViewMoreClick'
  | 'onFeedbackSubmit'
  | 'onError';

export type EventName =
  | 'agent_loaded'
  | 'agent_open'
  | 'agent_close'
  | 'agent_expand'
  | 'agent_restore'
  | 'agent_destroyed'
  | 'conversation_started'
  | 'conversation_restored'
  | 'conversation_reset'
  | 'prompt_submit'
  | 'suggestion_click'
  | 'refinement_displayed'
  | 'refinement_click'
  | 'response_started'
  | 'response_completed'
  | 'product_group_displayed'
  | 'product_displayed'
  | 'product_click'
  | 'add_to_basket_click'
  | 'add_to_basket_success'
  | 'add_to_basket_error'
  | 'view_more_click'
  | 'feedback_submit'
  | 'error';

export interface CallbackDefinition {
  callbackName: CallbackName;
  eventName: EventName;
  category: CallbackCategory;
  description: string;
  payloadFields: readonly string[];
  exampleData: Record<string, unknown>;
  exampleUsage: string;
}

const definition = (
  callbackName: CallbackName,
  eventName: EventName,
  category: CallbackCategory,
  description: string,
  payloadFields: readonly string[],
  exampleData: Record<string, unknown>,
): CallbackDefinition => ({
  callbackName,
  eventName,
  category,
  description,
  payloadFields,
  exampleData,
  exampleUsage: `function ${callbackName}(event) {\n  // ${description}\n  console.log(event.eventName, event.data);\n}`,
});

/**
 * The public host-callback contract. The agent dispatcher and the host
 * Integration Workbench both consume this registry so names and documentation
 * cannot drift apart.
 */
export const EVENT_DEFINITIONS: readonly CallbackDefinition[] = [
  definition('onAgentLoaded', 'agent_loaded', 'Agent lifecycle', 'The mounted agent is ready for host interaction.', ['sdkVersion', 'restoredMessages'], {sdkVersion: '1.5.0', restoredMessages: 2}),
  definition('onAgentOpen', 'agent_open', 'Agent lifecycle', 'The host or customer opens the existing agent instance.', ['source'], {source: 'api'}),
  definition('onAgentClose', 'agent_close', 'Agent lifecycle', 'The customer or host closes the agent without destroying it.', ['reason', 'source'], {reason: 'minimise', source: 'header'}),
  definition('onAgentExpand', 'agent_expand', 'Agent lifecycle', 'The agent enters expanded mode.', ['source'], {source: 'api'}),
  definition('onAgentRestore', 'agent_restore', 'Agent lifecycle', 'The agent returns from expanded mode.', ['source'], {source: 'api'}),
  definition('onAgentDestroyed', 'agent_destroyed', 'Agent lifecycle', 'The React instance has unmounted and released its host resources.', ['source'], {source: 'api'}),
  definition('onConversationStarted', 'conversation_started', 'Conversation lifecycle', 'Constructor has supplied a new, real conversation thread identifier.', ['originalPrompt', 'source'], {originalPrompt: 'Recommend a gift', source: 'input'}),
  definition('onConversationRestored', 'conversation_restored', 'Conversation lifecycle', 'A saved conversation is resumed using its initial thread identifier.', ['restoredMessages', 'source'], {restoredMessages: 3, source: 'conversation'}),
  definition('onConversationReset', 'conversation_reset', 'Conversation lifecycle', 'The active transcript and thread have been cleared.', ['source'], {source: 'api'}),
  definition('onPromptSubmit', 'prompt_submit', 'Customer input and refinements', 'The customer submits typed input.', ['originalPrompt', 'userInput', 'source'], {originalPrompt: 'A smoky whisky under £60', userInput: 'A smoky whisky under £60', source: 'input'}),
  definition('onSuggestionClick', 'suggestion_click', 'Customer input and refinements', 'The customer selects a landing-page or follow-up suggested question.', ['originalPrompt', 'suggestion', 'source'], {originalPrompt: 'Find memorable gifts by budget', suggestion: 'Find memorable gifts by budget', source: 'suggestion'}),
  definition('onRefinementDisplayed', 'refinement_displayed', 'Customer input and refinements', 'Constructor follow-up refinement options are visible to the customer.', ['question', 'options', 'originalPrompt', 'source'], {question: 'Which flavour do you prefer?', options: ['Fruity', 'Smoky'], source: 'refinement'}),
  definition('onRefinementClick', 'refinement_click', 'Customer input and refinements', 'The customer selects a rendered refinement option.', ['originalPrompt', 'option', 'source'], {originalPrompt: 'Smoky', option: 'Smoky', source: 'refinement'}),
  definition('onResponseStarted', 'response_started', 'Response lifecycle', 'The ASA response starts streaming for an accepted request.', ['originalPrompt', 'source'], {originalPrompt: 'Recommend a gift', source: 'input'}),
  definition('onResponseCompleted', 'response_completed', 'Response lifecycle', 'A complete ASA response has finished streaming.', ['originalPrompt', 'responseText', 'resultGroupCount', 'source'], {originalPrompt: 'Recommend a gift', responseText: 'Here are some ideas…', resultGroupCount: 3, source: 'conversation'}),
  definition('onProductGroupDisplayed', 'product_group_displayed', 'Product results', 'A product group becomes visible and is tracked once for its rendered response.', ['groupId', 'groupTitle', 'productCount', 'originalPrompt'], {groupId: 'result-123', groupTitle: 'Peated whisky', productCount: 3}),
  definition('onProductDisplayed', 'product_displayed', 'Product results', 'An individual product card becomes visible and is tracked once for its rendered response.', ['productId', 'productName', 'variationId', 'position', 'groupTitle'], {productId: '123', productName: 'Example bottle', variationId: '700ml', position: 1}),
  definition('onProductClick', 'product_click', 'Product results', 'The customer selects a product image, title or card.', ['productId', 'productName', 'variationId', 'position', 'interaction'], {productId: '123', productName: 'Example bottle', variationId: '700ml', position: 1, interaction: 'image'}),
  definition('onAddToBasketClick', 'add_to_basket_click', 'Add to Basket', 'The customer requests that a product be added to the host basket.', ['productId', 'productName', 'variationId', 'quantity', 'position'], {productId: '123', productName: 'Example bottle', quantity: 1, position: 1}),
  definition('onAddToBasketSuccess', 'add_to_basket_success', 'Add to Basket', 'The host basket action resolves successfully.', ['productId', 'productName', 'variationId', 'quantity', 'hostResult'], {productId: '123', productName: 'Example bottle', quantity: 1, hostResult: {success: true}}),
  definition('onAddToBasketError', 'add_to_basket_error', 'Add to Basket', 'The host basket action fails or rejects the request.', ['productId', 'productName', 'variationId', 'quantity', 'errorCode', 'errorMessage', 'recoverable'], {productId: '123', productName: 'Example bottle', quantity: 1, errorCode: 'BASKET_FAILED', errorMessage: 'Basket unavailable', recoverable: true}),
  definition('onViewMoreClick', 'view_more_click', 'Other interactions', 'The customer requests the host destination for the complete result group.', ['searchTerm', 'userInput', 'groupId', 'groupTitle', 'constructorRequest', 'filters', 'browse', 'sorting'], {searchTerm: 'single malt gifts', userInput: 'Recommend gifts', groupId: 'result-123', groupTitle: 'Gift ideas', constructorRequest: {term: 'single malt gifts'}}),
  definition('onFeedbackSubmit', 'feedback_submit', 'Other interactions', 'The customer rates a completed response.', ['feedback', 'originalPrompt'], {feedback: 'positive', originalPrompt: 'Recommend a gift'}),
  definition('onError', 'error', 'Other interactions', 'An agent, streaming, rendering, restoration, interaction or host-callback error is isolated for the host.', ['stage', 'errorCode', 'errorMessage', 'recoverable'], {stage: 'add_to_basket', errorCode: 'BASKET_FAILED', errorMessage: 'Basket unavailable', recoverable: true}),
] as const;

export const EVENT_DEFINITION_BY_CALLBACK = Object.fromEntries(
  EVENT_DEFINITIONS.map(definition => [definition.callbackName, definition]),
) as Record<CallbackName, CallbackDefinition>;

export const EVENT_DEFINITION_BY_EVENT_NAME = Object.fromEntries(
  EVENT_DEFINITIONS.map(definition => [definition.eventName, definition]),
) as Record<EventName, CallbackDefinition>;

export const CALLBACK_NAMES = EVENT_DEFINITIONS.map(definition => definition.callbackName) as readonly CallbackName[];
